import { clearLegacyRejectedFeePreviews } from "./clearLegacyRejectedFeePreviews";

export const AUTH_SESSION_CHANGE_EVENT = "asset:auth-session-change";
const AUTH_SESSION_CHANNEL_NAME = "asset:auth-session";
const AUTH_SESSION_COMMIT_STORAGE_KEY = "asset:auth-session-commit";
const LEGACY_AUTH_STORAGE_KEYS = ["token", "user", "impersonated", AUTH_SESSION_COMMIT_STORAGE_KEY] as const;

export type AuthSessionChangeReason =
  | "auth-check-failed"
  | "deployment-version"
  | "password-change"
  | "session-commit"
  | "session-cleared"
  | "signout"
  | "unauthorized";

export interface AuthSessionSnapshot {
  impersonated: unknown;
  token: null;
  user: unknown;
}

export interface AuthSessionChange {
  reason: AuthSessionChangeReason | "cross-tab-session-commit" | "cross-tab-session-cleared";
  snapshot: AuthSessionSnapshot;
  source: "cross-tab" | "same-tab";
}

type AuthSessionListener = (change: AuthSessionChange) => void;

let currentSession: AuthSessionSnapshot = { impersonated: null, token: null, user: null };
let sessionGeneration = 0;
let channel: BroadcastChannel | null = null;

export function purgeLegacyAuthStorage(): void {
  if (typeof localStorage === "undefined") return;
  for (const key of LEGACY_AUTH_STORAGE_KEYS) localStorage.removeItem(key);
}

function snapshot(): AuthSessionSnapshot {
  return { ...currentSession };
}

function getChannel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  channel ||= new BroadcastChannel(AUTH_SESSION_CHANNEL_NAME);
  return channel;
}

export function readAuthSession(): AuthSessionSnapshot {
  return snapshot();
}

export function hasAuthSession(): boolean {
  return currentSession.user !== null;
}

export function readAuthSessionGeneration(): number {
  return sessionGeneration;
}

export function setStoredAuthSession(user: unknown, impersonated: unknown = null): void {
  currentSession = { impersonated, token: null, user };
}

export function clearStoredAuthSession(): void {
  clearLegacyRejectedFeePreviews();
  purgeLegacyAuthStorage();
  currentSession = { impersonated: null, token: null, user: null };
}

export function emitAuthSessionChange(reason: AuthSessionChangeReason): void {
  sessionGeneration += 1;
  const event = new CustomEvent<AuthSessionChangeReason>(AUTH_SESSION_CHANGE_EVENT, { detail: reason });
  window.dispatchEvent(event);
  getChannel()?.postMessage({
    reason:
      reason === "session-cleared" || reason === "signout" ? "cross-tab-session-cleared" : "cross-tab-session-commit",
  });
}

export function subscribeToAuthSession(listener: AuthSessionListener): () => void {
  const handleSameTabChange = (event: Event) => {
    const reason = (event as CustomEvent<AuthSessionChangeReason>).detail;
    listener({ reason, snapshot: snapshot(), source: "same-tab" });
  };
  const authChannel = getChannel();
  const handleChannelChange = (event: MessageEvent<{ reason: AuthSessionChange["reason"] }>) => {
    sessionGeneration += 1;
    listener({
      reason: event.data?.reason || "cross-tab-session-commit",
      snapshot: snapshot(),
      source: "cross-tab",
    });
  };

  window.addEventListener(AUTH_SESSION_CHANGE_EVENT, handleSameTabChange);
  authChannel?.addEventListener("message", handleChannelChange);
  return () => {
    window.removeEventListener(AUTH_SESSION_CHANGE_EVENT, handleSameTabChange);
    authChannel?.removeEventListener("message", handleChannelChange);
  };
}
